import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading, Modal, NotificationAlert } from "src/components/common";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { ModalSize } from "src/enumerations/ModalSize";
import { TrialItemsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialItemsPageActions from "../../../actions/pages/trialItemsPage";
import * as TrialActions from "../../../actions/trial";
import * as TrialItemActions from "../../../actions/trialItem";
import * as TrialItemCategoryActions from "../../../actions/trialItemCategory";
import * as TrialItemTemplateActions from "../../../actions/trialItemTemplate";
import { SelectFormInput } from "../../../components/form";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcon, FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import CollectionInstanceComponent from "../../../containers/Collection/CollectionInstance/CollectionInstanceComponent";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { setModalTitle } from "../../../helpers/modalHelper";
import { TrialSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";
import TrialItemCreateVersion from "../TrialItemCreateVersion/TrialItemCreateVersion";

import "bootstrap/dist/js/bootstrap.bundle.min";

import "./TrialItemsPage.scss";

interface TrialItemsPageProps {
    trial: Dtos.Trial;
    loadingTrial: boolean;
    loadTrialSuccess: boolean;
    loadTrialFailure: boolean;
    loadTrialErrorCode: string;
    trialItemCategories: Dtos.TrialItemCategory[];
    loadingTrialItemCategories: boolean;
    loadTrialItemCategoriesSuccess: boolean;
    loadTrialItemCategoriesFailure: boolean;
    loadTrialItemCategoriesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    collectionInstanceModalOpen: boolean;
    collectionInstanceId: number;
    modalTitle: string;
    modalDescription: string;
    trialItemTemplates: Dtos.TrialItemTemplate[];
    loadingTrialItemTemplates: boolean;
    loadTrialItemTemplatesSuccess: boolean;
    loadTrialItemTemplatesFailure: boolean;
    loadTrialItemTemplatesErrorCode: string;
    savingTrialItem: boolean;
    saveTrialItemSuccess: boolean;
    createVersionModalOpen: boolean;
    createVersionTrialItem: Dtos.TrialItem;
    clearTrial: TrialActions.ITrialClearActionCreator;
    loadTrial: TrialActions.ITrialLoadByIdActionCreator;
    clearTrialItemCategories: TrialItemCategoryActions.ITrialItemCategoriesClearActionCreator;
    loadTrialItemCategories: TrialItemCategoryActions.ITrialItemCategoriesLoadByTrialIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearTrialItemTemplates: TrialItemTemplateActions.ITrialItemTemplateClearActionCreator;
    loadTrialItemTemplates: TrialItemTemplateActions.ITrialItemTemplateLoadActionCreator;
    saveTrialItem: TrialItemActions.ITrialItemSaveActionCreator;
    setEditModelViewState: TrialItemsPageActions.ITrialItemsPageSetEditModalViewStateActionCreator;
    setCreateVersionModelViewState: TrialItemsPageActions.ITrialItemsPageSetCreateVersionModalViewStateActionCreator;
}

const TrialItemsPage: FC<TrialItemsPageProps> = ({
    trial,
    loadingTrial,
    loadTrialSuccess,
    loadTrialFailure,
    loadTrialErrorCode,
    trialItemCategories,
    loadingTrialItemCategories,
    loadTrialItemCategoriesSuccess,
    loadTrialItemCategoriesFailure,
    loadTrialItemCategoriesErrorCode,
    permissions,
    collectionInstanceModalOpen,
    collectionInstanceId,
    modalTitle,
    modalDescription,
    trialItemTemplates,
    loadingTrialItemTemplates,
    loadTrialItemTemplatesSuccess,
    loadTrialItemTemplatesFailure,
    loadTrialItemTemplatesErrorCode,
    savingTrialItem,
    saveTrialItemSuccess,
    createVersionModalOpen,
    createVersionTrialItem,
    clearTrial,
    loadTrial,
    clearTrialItemCategories,
    loadTrialItemCategories,
    setBreadcrumbs,
    setDrawerItems,
    clearTrialItemTemplates,
    loadTrialItemTemplates,
    saveTrialItem,
    setEditModelViewState,
    setCreateVersionModelViewState
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const trialIdNumber = parseInt(trialId, 10);

    //
    // -- States --
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [addTrialItemId, setAddTrialItemId] = useState<number | undefined>(undefined);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] = useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showTrialItems =
        !loadingTrial &&
        loadTrialSuccess &&
        !loadingTrialItemTemplates &&
        loadTrialItemTemplatesSuccess &&
        !loadingTrialItemCategories &&
        loadTrialItemCategoriesSuccess &&
        !creatingTree;


    // Load Trial, Items and Templates
    useEffect(() => {
        if (trialIdNumber && !trial) {
            setBreadcrumbs(TrialItemsBreadcrumbs(trialIdNumber));
            setDrawerItems(TrialSubMenu(trialIdNumber, permissions));

            loadTrial(trialIdNumber);
            loadTrialItemTemplates(trialIdNumber);
            loadTrialItemCategories(trialIdNumber);
        }
    }, [
        trialIdNumber,
        permissions,
        trial,
        setBreadcrumbs,
        setDrawerItems,
        loadTrial,
        TrialItemsBreadcrumbs,
        TrialSubMenu,
        loadTrialItemTemplates,
        loadTrialItemCategories
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialFailure) {
            navigate(`/error/${loadTrialErrorCode}`);
        }
        if (loadTrialItemTemplatesFailure) {
            navigate(`/error/${loadTrialItemTemplatesErrorCode}`);
        }
        if (loadTrialItemCategoriesFailure) {
            navigate(`/error/${loadTrialItemCategoriesErrorCode}`);
        }
    }, [
        loadTrialFailure,
        loadTrialItemTemplatesFailure,
        loadTrialErrorCode,
        loadTrialItemTemplatesErrorCode,
        loadTrialItemCategoriesFailure,
        loadTrialItemCategoriesErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialIdNumber && trial?.displayName) {
            setDrawerItems(TrialSubMenu(trialIdNumber, permissions));
            setBreadcrumbs(
                TrialItemsBreadcrumbs(trialIdNumber, trial.displayName)
            );
        }
    }, [
        trialIdNumber,
        trial,
        permissions,
        TrialSubMenu,
        TrialItemsBreadcrumbs,
        setDrawerItems,
        setDrawerItems,
    ]);

    // Cleanup effect to clear the trial item on component unmount
    useEffect(() => {
        return () => {
            clearTrial();
            clearTrialItemTemplates();
            clearTrialItemCategories();
        };
    }, [clearTrial, clearTrialItemTemplates, clearTrialItemCategories]);

    // Reload trial & trial items after saving trial item
    useEffect(() => {
        if (!savingTrialItem && saveTrialItemSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial item saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );
            loadTrial(trialIdNumber);
            loadTrialItemTemplates(trialIdNumber);
            loadTrialItemCategories(trialIdNumber);
            setInitalizedTreeData(false);
        }
    }, [
        savingTrialItem,
        saveTrialItemSuccess,
        trialIdNumber,
        loadTrial,
        loadTrialItemTemplates,
        loadTrialItemCategories,
        setInitalizedTreeData,
    ]);

    // Refresh tree
    useEffect(() => {
        if (trialItemCategories) {
            setInitalizedTreeData(false);
        }
    }, [trialItemCategories, setInitalizedTreeData]);

    const handleModalClose = () => {
        setIsOpen(false);
        setAddTrialItemId(undefined);
    };

    const handleModalSubmit = async () => {
        if (!addTrialItemId || !trialIdNumber) {
            Alert.error(
                <NotificationAlert
                    alertContent={<span>An error occurred saving the trial item</span>}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
            return;
        }
        let template = trialItemTemplates.find((x) => x.id === addTrialItemId);
        let trialItem: Dtos.TrialItem = {
            trialId: trialIdNumber,
            trialItemTemplateId: addTrialItemId,
            version: template?.initialVersion ?? null,
            active: true,
        } as Dtos.TrialItem;
        await saveTrialItem(trialItem, null);

        setIsOpen(false);
        setAddTrialItemId(undefined);
    };

    // Create tree
    useEffect(() => {
        if (trialItemCategories && !initalizedTreeData) {
            setCreatingTree(true);

            // If no trial items return
            if (trialItemCategories?.length == 0) {
                setTreeData(null);
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (
                trialItemSummaries: Dtos.TrialItemSummary[]
            ) => {
                let children = [];
                const trialedSummaries = Object.values<Dtos.TrialItemSummary>(
                    trialItemSummaries.reduce((acc, item) => {
                        const key = `${item.trialId}-${item.trialItemTemplateId}`;

                        if (!acc[key] || new Date(item.dateEntered) > new Date(acc[key].dateEntered)) {
                            acc[key] = item;
                        }

                        return acc;
                    }, {})
                );

                children =
                    trialedSummaries
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((y) => ({
                            id: `item-${y.id}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "1fr 3fr 3fr 3fr 2fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.FILE} />
                                    </div>
                                    <div className="px-1">{y?.name}</div>
                                    <div className="d-inline-block px-1 text-truncate">
                                        <span
                                            className="text-truncate"
                                            data-tip={y?.description}
                                        >
                                            {y?.description}
                                            <ReactTooltip />
                                        </span>
                                    </div>
                                    <div className="px-1">
                                        {y?.version && (
                                            <button
                                                disabled={
                                                    !AuthenticationHelper.hasPermission(
                                                        permissions,
                                                        Dtos.Permission
                                                            .TrialItemUpdate,
                                                        null,
                                                        null
                                                    )
                                                }
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm btn-row-version"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (y?.collectionInstanceId) {
                                                        setEditModelViewState(
                                                            true,
                                                            y.collectionInstanceId,
                                                            y?.name,
                                                            y?.description
                                                        );
                                                    }
                                                }}
                                            >
                                                <span>{y.version}</span>
                                            </button>
                                        )}
                                        {y?.trialItemSummaryVersions?.map(
                                            (item, index) => (
                                                <button
                                                    key={`version-button-${item?.version}-${index}`}
                                                    className="btn btn-secondary btn-sm btn-row-version"
                                                    type="button"
                                                    disabled={
                                                        !AuthenticationHelper.hasPermission(
                                                            permissions,
                                                            Dtos.Permission
                                                                .TrialItemUpdate,
                                                            null,
                                                            null
                                                        )
                                                    }
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (item?.collectionInstanceId) {
                                                            setEditModelViewState(
                                                                true,
                                                                item.collectionInstanceId,
                                                                y?.name,
                                                                y?.description
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <span>{item.version}</span>
                                                </button>
                                            )
                                        )}
                                    </div>
                                    <div className="px-1 d-flex align-items-start justify-content-end">
                                        {y?.allowVersions && (
                                            <button
                                                disabled={false}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const trialItems = trialItemCategories?.flatMap(x => x.trialItems) ?? [];
                                                    setCreateVersionModelViewState(
                                                        true,
                                                        trialItems.find((x) => x.id == y.id)
                                                    );
                                                }}
                                                type="button"
                                                className="btn btn-outline-secondary mr-2 btn-sm"
                                            >
                                                <span>New version</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 3fr 3fr 2fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Name</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Description</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Version</strong>
                                </div>
                                <div className="px-1"></div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                [...trialItemCategories]
                    ?.sort((a, b) => a?.category?.rank - b?.category?.rank)
                    ?.map((x) => ({
                        id: `category-${x?.category?.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ROLE} />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "400px" }}
                                >
                                    {x?.category?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        { [...new Set(x?.trialItems?.map((x) => x.trialItemTemplateId)),]?.length }
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.trialItemSummaries),],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        trialItemCategories,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (id.includes("category")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                } else if (id.includes("item")) {
                    const trialItems = trialItemCategories?.flatMap(x => x.trialItems) ?? [];
                    const selectedTrialItem: Dtos.TrialItem = trialItems?.find((x) => x.id?.toString() == id.replace("item-", ""));
                    setEditModelViewState(
                        true,
                        selectedTrialItem?.collectionInstanceId,
                        selectedTrialItem?.trialItemTemplate?.name,
                        selectedTrialItem?.trialItemTemplate?.description
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, permissions, trialItemCategories]
    );

    const renderCreateVersionModal = useMemo(() => {
        if (trial && !loadingTrial) {
            return (
                <Modal
                    open={createVersionModalOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setCreateVersionModelViewState(false, null);
                    }}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {setModalTitle(undefined, "Create Version")}
                        </h5>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setCreateVersionModelViewState(false, null);
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div
                        className="modal-body"
                        style={{ paddingTop: 0, paddingBottom: 0 }}
                    >
                        <TrialItemCreateVersion
                            trialId={trialIdNumber}
                            currentTrialItem={createVersionTrialItem}
                            onClose={() => {
                                setCreateVersionModelViewState(false, null);
                            }}
                        />
                    </div>
                </Modal>
            );
        }
    }, [
        trialIdNumber,
        loadingTrial,
        createVersionModalOpen,
        setCreateVersionModelViewState,
        createVersionTrialItem,
    ]);

    const renderItemModal = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.TrialItemUpdate,
            undefined,
            undefined
        );
        const formDisabled = !hasPermission;

        if (collectionInstanceId && collectionInstanceId > 0) {
            return (
                <Modal
                    open={collectionInstanceModalOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setEditModelViewState(false, null, "", "");
                    }}
                    size={ModalSize.Xl}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h5 className="mb-0">
                                {setModalTitle(undefined, modalTitle)}
                            </h5>
                            {modalDescription ? (
                                <small> 
                                    {modalDescription}
                                </small>
                            ) : null}
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setEditModelViewState(false, null, "", "");
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div className="modal-body customscrollbar">
                        <CollectionInstanceComponent 
                            collectionInstanceId={collectionInstanceId}
                            readonly={formDisabled}
                            onClose={() => {
                                setEditModelViewState(false, null, "", "");
                            }}
                            onSaved={() => {
                                loadTrialItemCategories(trialIdNumber);
                            }}
                        />
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        collectionInstanceId,
        collectionInstanceModalOpen,
        permissions,
        modalTitle,
        modalDescription,
        trialIdNumber,
        setEditModelViewState,
        loadTrialItemCategories,
    ]);

    const selectOptions = useMemo(() => {
        if (!trialItemTemplates || !trialItemCategories) return [];

        const trialItems = trialItemCategories?.flatMap(x => x.trialItems) ?? [];
        const currentIds = [...new Set(trialItems?.map((x) => x.trialItemTemplateId))];

        var options = trialItemTemplates
            ?.filter((x) => !currentIds.includes(x.id))
            ?.map((x) => ({ value: x.id, label: x?.initialVersion ? `${x?.name} | ${x.initialVersion}` : x?.name }));

        return options?.sort((a, b) => a?.label?.localeCompare(b?.label));
    }, [trialItemTemplates, trialItemCategories]);

    return (
        <RestrictedLayout
            loading={loadingTrial || loadingTrialItemTemplates || loadingTrialItemCategories}
            permission={Dtos.Permission.TrialItemView}
        >
            <div>
                <div className="row">
                    <div className={"col mb-2"}>
                        <h1>Trial Items</h1>
                    </div>
                    <div className="col-auto">
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TrialItemCreate,
                            trialIdNumber,
                            null
                        ) && (
                            <>
                                <div className="btn-group">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => setIsOpen(true)}
                                    >
                                        Add Item Template
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary dropdown-toggle dropdown-toggle-split"
                                        data-toggle="dropdown"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                    >
                                        <span className="sr-only">
                                            Toggle Dropdown
                                        </span>
                                    </button>
                                    <div className="dropdown-menu dropdown-menu-right">
                                        <a
                                            className="dropdown-item pointer"
                                            onClick={() =>
                                                navigate(`/settings/trial/itemtemplates/${trialIdNumber}/template/create`)
                                            }
                                        >
                                            Create Item Template
                                        </a>
                                        <a
                                            className="dropdown-item pointer"
                                            onClick={() =>
                                                navigate(`/settings/trial/itemtemplates/${trialIdNumber}`)
                                            }
                                        >
                                            Edit Item Templates
                                        </a>
                                        <div className="dropdown-divider"></div>
                                        <a
                                            className="dropdown-item pointer"
                                            onClick={() => setIsOpen(true)}
                                        >
                                            Add Item Template
                                        </a>
                                    </div>
                                </div>

                                <Modal
                                    open={isOpen}
                                    disableCloseOnOverlayClick={true}
                                    onClose={handleModalClose}
                                    size={ModalSize.Lg}
                                >
                                    <div className="modal-header">
                                        <h5 className="modal-title">
                                            Add Trial Item
                                        </h5>
                                        <button
                                            type="button"
                                            className="close"
                                            aria-label="Close"
                                            onClick={handleModalClose}
                                        >
                                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                                        </button>
                                    </div>
                                    <div className="modal-body p-3">
                                        <div className="form-row pb-2">
                                            <div className="col-12">
                                                {selectOptions?.length === 0 ? (
                                                    <>
                                                        <span>
                                                            No <em>Trial Item Templates</em> are available for <strong>{trial?.displayName}</strong>.
                                                        </span>
                                                        <br />
                                                        <button
                                                            className="btn btn-primary mr-1 mt-4"
                                                            onClick={() =>
                                                                navigate(`/settings/trial/itemtemplates/${trialIdNumber}/template/create`)
                                                            }
                                                        >
                                                            Create Item Template
                                                        </button>
                                                    </>
                                                ) : (
                                                    <SelectFormInput
                                                        loading={loadingTrialItemTemplates}
                                                        multi={false}
                                                        options={selectOptions}
                                                        clearable={true}
                                                        backspaceRemoves={false}
                                                        name={"addTrialItemId"}
                                                        id={"addTrialItemId"}
                                                        placeholder="Trial Item"
                                                        onChange={function (value: any, _options?: any): any {
                                                            setAddTrialItemId(
                                                                value != undefined
                                                                    ? parseInt(value)
                                                                    : undefined
                                                            );
                                                        }}
                                                        value={addTrialItemId}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-row pt-2">
                                            <div className="col-12 d-flex justify-content-end">
                                                {selectOptions?.length > 0 && (
                                                    <button
                                                        className="btn btn-primary mr-1"
                                                        disabled={savingTrialItem}
                                                        onClick={handleModalSubmit}
                                                    >
                                                        Submit
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-outline-primary ml-1"
                                                    onClick={handleModalClose}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Modal>
                            </>
                        )}
                    </div>
                </div>
                {!showTrialItems ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialItems ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                                    </div>
                                    <span>
                                        There are currently no items to view.
                                    </span>
                                </div>
                            </div>
                        )}
                        {renderItemModal}
                        {renderCreateVersionModal}
                    </>
                ) : null}
            </div>
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialItemsPageProps> => {
    let trial: Dtos.Trial | undefined = !(
        state.trials.formData instanceof Array
    )
        ? state.trials.formData
        : undefined;
    let trialItemCategories: Dtos.TrialItemCategory[] | undefined = (state.trialItemCategory.formData instanceof Array)
        ? state.trialItemCategory.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trial: trial!,
        loadingTrial: state.trials.loadState && state.trials.loadState.status == RequestState.Pending,
        loadTrialSuccess: state.trials.loadState && state.trials.loadState.status == RequestState.Success,
        loadTrialFailure: state.trials.loadState && state.trials.loadState.status == RequestState.Failure,
        loadTrialErrorCode: state.trials.loadState.errorCode,
        trialItemCategories: trialItemCategories!,
        loadingTrialItemCategories: state.trialItemCategory.loadState && state.trialItemCategory.loadState.status == RequestState.Pending,
        loadTrialItemCategoriesSuccess: state.trialItemCategory.loadState && state.trialItemCategory.loadState.status == RequestState.Success,
        loadTrialItemCategoriesFailure: state.trialItemCategory.loadState && state.trialItemCategory.loadState.status == RequestState.Failure,
        loadTrialItemCategoriesErrorCode: state.trialItemCategory.loadState.errorCode,
        permissions: permissions,
        collectionInstanceModalOpen: state.trialItemsPage.editModalOpen,
        collectionInstanceId: state.trialItemsPage.collectionInstanceId,
        modalTitle: state.trialItemsPage.name,
        modalDescription: state.trialItemsPage.description,
        trialItemTemplates: state.trialItemTemplate.formFilteredData!,
        loadingTrialItemTemplates: state.trialItemTemplate.loadState && state.trialItemTemplate.loadState.status == RequestState.Pending,
        loadTrialItemTemplatesSuccess: state.trialItemTemplate.loadState && state.trialItemTemplate.loadState.status == RequestState.Success,
        loadTrialItemTemplatesFailure: state.trialItemTemplate.loadState && state.trialItemTemplate.loadState.status == RequestState.Failure,
        loadTrialItemTemplatesErrorCode: state.trialItemTemplate.loadState.errorCode,
        savingTrialItem: state.trialItem.saveState && state.trialItem.saveState.status == RequestState.Pending,
        saveTrialItemSuccess: state.trialItem.saveState && state.trialItem.saveState.status == RequestState.Success,
        createVersionModalOpen: state.trialItemsPage.createVersionModalOpen,
        createVersionTrialItem: state.trialItemsPage.trialItem,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialItemsPageProps> => {
    return {
        loadTrial: bindActionCreators(TrialActions.LoadTrialById, dispatch),
        clearTrial: bindActionCreators(TrialActions.Clear, dispatch),
        loadTrialItemTemplates: bindActionCreators(TrialItemTemplateActions.LoadTrialItemTemplate, dispatch),
        clearTrialItemTemplates: bindActionCreators(TrialItemTemplateActions.Clear, dispatch),
        saveTrialItem: bindActionCreators(TrialItemActions.SaveTrialItem, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        setEditModelViewState: bindActionCreators(TrialItemsPageActions.setEditModalViewState, dispatch),
        setCreateVersionModelViewState: bindActionCreators(TrialItemsPageActions.setCreateVersionModalViewState, dispatch),
        loadTrialItemCategories: bindActionCreators(TrialItemCategoryActions.LoadTrialItemCategoriesByTrialId, dispatch),
        clearTrialItemCategories: bindActionCreators(TrialItemCategoryActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialItemsPage);
